import type { OpId } from "./ids.ts";

/**
 * Rectangles are normalized to 0..1 of the frame so that they stay correct when the
 * same op is replayed against a proxy, a thumbnail, or the full-resolution original.
 */
export type NormRect = {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
};

export type ResizeFit = "contain" | "cover" | "stretch";
export type FadeCurve = "linear" | "equalPower" | "exponential";
export type FadeDirection = "in" | "out";
export type NormalizeMode = "peak" | "lufs";
export type EqFilter = "peaking" | "lowShelf" | "highShelf" | "lowPass" | "highPass";

export type EqBand = {
  readonly filter: EqFilter;
  readonly frequencyHz: number;
  readonly gainDb: number;
  readonly q: number;
};

export type OpParams =
  | { readonly t: "crop"; readonly rect: NormRect }
  | {
      readonly t: "resize";
      readonly widthPx: number;
      readonly heightPx: number;
      readonly fit: ResizeFit;
    }
  | { readonly t: "rotate"; readonly quarterTurns: 0 | 1 | 2 | 3 }
  | { readonly t: "flip"; readonly horizontal: boolean; readonly vertical: boolean }
  | { readonly t: "grayscale" }
  | {
      readonly t: "adjust";
      readonly brightness: number;
      readonly contrast: number;
      readonly saturation: number;
      readonly hueDeg: number;
    }
  | { readonly t: "blur"; readonly radiusPx: number }
  | { readonly t: "sharpen"; readonly amount: number }
  | { readonly t: "mosaic"; readonly regions: readonly NormRect[]; readonly blockPx: number }
  | {
      readonly t: "chromaKey";
      /** The colour to erase, as linear 0..1 RGB in the image's own space. */
      readonly color: readonly [number, number, number];
      /** How far a pixel may differ and still be erased, 0..1. */
      readonly tolerance: number;
      /** Width of the fade past the tolerance, so edges are not jagged. */
      readonly softness: number;
    }
  | { readonly t: "gain"; readonly db: number }
  | { readonly t: "normalize"; readonly mode: NormalizeMode; readonly targetDb: number }
  /** Ramps across its own range: which way it goes and how, nothing about how long. */
  | { readonly t: "fade"; readonly direction: FadeDirection; readonly curve: FadeCurve }
  | { readonly t: "eq"; readonly bands: readonly EqBand[] }
  /** Keeps only its own range. Carries no parameters: the range is the whole step. */
  | { readonly t: "trim" }
  | { readonly t: "speed"; readonly rate: number; readonly preservePitch: boolean }
  | { readonly t: "toMono" };

export type OpKind = OpParams["t"];

/** Seconds from the start of the material. */
export type OpRange = { readonly inSec: number; readonly outSec: number };

export type Op = OpParams & {
  readonly id: OpId;
  readonly enabled: boolean;
  /**
   * Where the step applies. Absent means the whole material, which is what a step starts
   * as; giving it a range is how "fade in at 0:12" is expressed.
   */
  readonly range?: OpRange | undefined;
};

/**
 * Steps that can be limited to a range. A mono fold changes the channel count, so it
 * only makes sense applied to everything.
 */
export const RANGEABLE_OPS: ReadonlySet<OpKind> = new Set<OpKind>([
  "gain",
  "normalize",
  "fade",
  "eq",
  "trim",
]);

export type OpOf<K extends OpKind> = Extract<Op, { t: K }>;

export const FULL_FRAME: NormRect = { x: 0, y: 0, w: 1, h: 1 };

export const DEFAULT_OP_PARAMS: { readonly [K in OpKind]: Extract<OpParams, { t: K }> } = {
  crop: { t: "crop", rect: FULL_FRAME },
  resize: { t: "resize", widthPx: 0, heightPx: 0, fit: "contain" },
  rotate: { t: "rotate", quarterTurns: 0 },
  flip: { t: "flip", horizontal: false, vertical: false },
  grayscale: { t: "grayscale" },
  adjust: { t: "adjust", brightness: 0, contrast: 0, saturation: 0, hueDeg: 0 },
  blur: { t: "blur", radiusPx: 0 },
  sharpen: { t: "sharpen", amount: 0 },
  mosaic: { t: "mosaic", regions: [], blockPx: 16 },
  chromaKey: { t: "chromaKey", color: [1, 1, 1], tolerance: 0.12, softness: 0.06 },
  gain: { t: "gain", db: 0 },
  // -14 LUFS is the loudness most streaming platforms normalize to.
  normalize: { t: "normalize", mode: "lufs", targetDb: -14 },
  fade: { t: "fade", direction: "in", curve: "equalPower" },
  eq: { t: "eq", bands: [] },
  speed: { t: "speed", rate: 1, preservePitch: true },
  toMono: { t: "toMono" },
  trim: { t: "trim" },
};

export function isIdentityOp(op: OpParams): boolean {
  switch (op.t) {
    case "crop":
      return op.rect.x === 0 && op.rect.y === 0 && op.rect.w === 1 && op.rect.h === 1;
    case "rotate":
      return op.quarterTurns === 0;
    case "flip":
      return !op.horizontal && !op.vertical;
    case "adjust":
      return op.brightness === 0 && op.contrast === 0 && op.saturation === 0 && op.hueDeg === 0;
    case "blur":
      return op.radiusPx === 0;
    case "sharpen":
      return op.amount === 0;
    case "mosaic":
      return op.regions.length === 0;
    case "chromaKey":
      return op.tolerance === 0;
    case "gain":
      return op.db === 0;
    case "eq":
      return op.bands.every((band) => band.gainDb === 0);
    case "speed":
      return op.rate === 1;
    case "fade":
    case "resize":
    case "grayscale":
    case "normalize":
    case "toMono":
    case "trim":
      return false;
  }
}
